import React, { useContext, useEffect, useMemo, useState } from "react";
import { nanoid } from "nanoid";
import Search from "../../CommonComponents/Search";
import InputDropdown from "../../CommonComponents/InputDropdown";
import Button from "../../Button";
import Table from "../../CommonComponents/Table";
import useAuth from "../../../Hooks/useAuth";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useDebouncedValue } from "../../../Hooks/useDebounceValue";
import useAxiosPrivate from "../../../Hooks/useAxiosPrivate";
import { libTypeIds, RESULT_STATUS } from "../../../constants";
import Loading from "../../Notification-Loading/Loading/Loading";
import Error404 from "../../../Error404";
import ToggleButton from "../../CommonComponents/ToggleButton";
import Tooltip from "../../CommonComponents/Tooltip";
import useS3FileManagement from "../../../Hooks/useS3FileManagement";
import { LibraryContext } from "../../../context/LibraryContext";

const TestList = ({ mockuse = 0 }) => {
  const { navigate, isLoaded, setIsLoaded, setShowNotify } = useAuth();
  const { searchText, setSearchText, setTopics } = useContext(LibraryContext);
  const { type } = useParams();
  const [searchParams] = useSearchParams();
  const { getPreSignedUrl } = useS3FileManagement();
  const selectable = searchParams.get("selectable") === "true" || false;
  const lpId = searchParams.get("lpId") || null;
  const moduleId = searchParams.get("moduleId") || null;
  const chapterId = searchParams.get("chapterId") || null;
  const batchId = searchParams.get("bId") || null;
  const topId = searchParams.getAll("topId") || null;
  const isVideoInterview = searchParams.get("isVideoInterview") || null;
  const isAiInterview = searchParams.get("isAiInterview") || null;
  const baseRoute = batchId ? `/batches/${batchId}/courses` : "/learningpath";

  const axios = useAxiosPrivate();
  const [data, setData] = useState([]);
  const [filteredData, setFilterData] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [selectedRow, setSelectedRow] = useState([]);
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    setSelectedTopics(topId.map(Number));
  }, [searchParams]);

  const onChangeTestStatus = async (value, id) => {
    try {
      setIsLoaded(true);
      let mockValue = value === 1 ? 2 : 1;
      let response = await axios.put(
        `node/admin/test/settings/update/${id}`,
        JSON.stringify({ activateMock: mockValue }),
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (response.data.resultCode === RESULT_STATUS.SUCCESS) {
        setData((prev) =>
          prev.map((item) =>
            item.id === id ? { ...item, mockuse: mockValue } : item,
          ),
        );

        setFilterData((prev) =>
          prev.map((item) =>
            item.id === id ? { ...item, mockuse: mockValue } : item,
          ),
        );
      } else {
        setShowNotify({
          title: "Warning !",
          msg:
            response.data.msg ??
            `Unable to change the status. Please try again later`,
          show: true,
        });
      }
    } catch (error) {
      console.error("error in change test status", error);
      let isUnAuth = error.message.includes("403");
      setShowNotify({
        isUnAuth,
        title: isUnAuth ? "Warning !" : "Error !",
        msg: isUnAuth
          ? "You have been logged-out due to inactivity. Login again."
          : error.message ??
            `Unable to change the status. Please try again later`,
        show: true,
      });
      // getCatchBlockDetails(error);
    } finally {
      setIsLoaded(false);
    }
  };

  const onClickLink = async (event, path) => {
    event.preventDefault();
    event.stopPropagation();
    const { url: presignedUrl } = await getPreSignedUrl(
      path,
      undefined,
      path.split(".").pop(),
      "GET",
    );
    let url = presignedUrl;
    window.open(url, "_blank");
  };

  const columns = useMemo(() => {
    let data = [libTypeIds.CONTENT, libTypeIds.ASSIGNMENT].includes(
      parseInt(type),
    )
      ? [
          {
            key: "title",
            title: "Title",
            render: (col) => <Tooltip title={col}>{col}</Tooltip>,
          },
          {
            key: "topics",
            title: "Topic",
            render: (col) => <Tooltip title={col}>{col}</Tooltip>,
          },
          {
            key: "createdby",
            title: "Createdby",
            render: (col) => (!col || col === "null" ? "-" : col),
          },
          {
            key: "libAttachments",
            title: "Attachments",
            render: (col, row) => (
              <ul>
                {col?.map((value) => {
                  let index = value?.lastIndexOf("/");
                  return (
                    value && (
                      <li key={nanoid()}>
                        <Link
                          to={`${process.env.REACT_APP_URL}${value?.slice(
                            index + 1,
                          )}`}
                          onClick={(event) => onClickLink(event, value)}
                        >
                          {value?.slice(index + 1)}
                        </Link>
                      </li>
                    )
                  );
                })}
              </ul>
            ),
          },
        ]
      : [
          {
            key: "title",
            title: "Test",
            render: (col) => <Tooltip title={col}>{col}</Tooltip>,
          },
          {
            key: "topics",
            title: "Topic",
            render: (col) => <Tooltip title={col}>{col}</Tooltip>,
          },
          {
            key: "qncount",
            title: "No. of Question",
          },
          {
            key: "duration",
            title: "Duration(mins)",
          },
        ];
    if (mockuse === 1)
      data.push({
        key: "mockuse",
        title: "Active",
        render: (col, row) => (
          <ToggleButton
            size="sm"
            pressed={col === 2}
            onChange={(value) => onChangeTestStatus(col, row.id)}
          />
        ),
      });
    return data;
  }, [mockuse, type]);

  const testName = useMemo(() => {
    return parseInt(type) === libTypeIds.CONTENT
      ? "Contents"
      : parseInt(type) === libTypeIds.ASSIGNMENT
      ? "Assignments"
      : parseInt(type) === libTypeIds.MCQ
      ? "MCQ Tests"
      : parseInt(type) === libTypeIds.CODING_CHALLENGE
      ? "Coding Tests"
      : parseInt(type) === libTypeIds.SQL
      ? "SQL Tests"
      : parseInt(type) === libTypeIds.DESCRIPTIVE && JSON.parse(isVideoInterview)
      ? "Video Tests"
      : parseInt(type) === libTypeIds.DESCRIPTIVE
      ? "Descriptive Tests"
      : parseInt(type) === libTypeIds.CODING_SNIPPET
      ? "Coding Snippet Tests"
      : mockuse === 1
      ? "Mock Tests"
      : null;
  }, [type, mockuse, isVideoInterview]);

  const debouncedSearch = useDebouncedValue(searchText, 500);
  useEffect(() => {
    setData([]);
    setTopics([]);
    setSearchText("");
    setSelectedTopics([]);
  }, [type, setTopics, setSearchText]);

  useEffect(() => {
    const getTestList = async () => {
      try {
        setIsLoaded(true);
        const { data } = await axios.get(
          `node/admin/test?libtypeid=${Number(type)}&isVideoQns=${JSON.parse(
            isVideoInterview,
          )}&mockuse=${mockuse}&aiMode=${isAiInterview}`,
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        );
        if (data.resultCode === RESULT_STATUS.SUCCESS) {
          let tests =
            typeof data.data.tests === "string" ||
            typeof data.data?.data === "string"
              ? JSON.parse(data.data?.tests ?? data.data?.data ?? "[]")
              : data.data?.tests ?? data.data?.data ?? [];
          // setTests(tests);
          let transformedData = tests.map((test) => ({
            ...test,
            topics: test.topics?.map((top) => top.topicname).join(", "),
            topicsList: test.topics,
            libAttachments:
              test.topics?.flatMap(
                (top) =>
                  top.libattachments?.map((att) => att.attachments) ?? [],
              ) ?? [],
          }));
          setData(transformedData);
          setFilterData(transformedData);

          let topics = [];

          for (let i = 0; i < tests.length; i++) {
            for (let j = 0; j < tests[i].topics.length; j++) {
              topics.push({
                value: tests[i].topics[j].topicid,
                label: tests[i].topics[j].topicname,
              });
              topics = [...new Map(topics.map((m) => [m.value, m])).values()];
            }
          }

          setTopics(topics);
        } else {
          setShowNotify({
            title: "Warning !",
            msg:
              data?.data?.msg ??
              `Unable to get the ${testName}. Please try again later`,
            show: true,
          });
        }
      } catch (error) {
        console.error("error in get test list", error);
        let isUnAuth = error.message.includes("403");
        setShowNotify({
          isUnAuth,
          title: isUnAuth ? "Warning !" : "Error !",
          msg: isUnAuth
            ? "You have been logged-out due to inactivity. Login again."
            : error.message ??
              `Unable to get the ${testName}. Please try again later`,
          show: true,
        });
      } finally {
        setIsLoaded(false);
      }
    };
    getTestList();
  }, [
    type,
    axios,
    mockuse,
    setIsLoaded,
    testName,
    setShowNotify,
    isVideoInterview,
    isAiInterview,
    setTopics,
  ]);
const [isSearching, setIsSearching] = useState(false);

useEffect(() => {
  setIsSearching(true);
}, [searchText]);
  useEffect(() => {
    let filtered = data;

    // 🔍 Search filter
    if (debouncedSearch) {
      filtered = filtered.filter((test) =>
        test.title?.toLowerCase().includes(debouncedSearch.toLowerCase()),
      );
    }

    // 🏷 Topic filter
    if (selectedTopics.length > 0) {
      filtered = filtered.filter((test) =>
        test.topicsList?.some((t) => selectedTopics.includes(t.topicid)),
      );
    }

    setFilterData(filtered);
    setIsSearching(false);
  }, [debouncedSearch, selectedTopics, data]);

  const onClick = () => {
    const isVideoQnsBool = isVideoInterview === "true";

    let queryString = `${
      selectable
        ? `?selectable=${selectable}${lpId ? `&lpId=${lpId}` : ""}${
            moduleId ? `&moduleId=${moduleId}` : ""
          }${chapterId ? `&chapterId=${chapterId}` : ""}${
            batchId ? `&bId=${batchId}` : ""
          }${isVideoQnsBool ? "&isVideoInterview=true" : ""}`
        : `${isVideoQnsBool ? "?isVideoInterview=true" : ""}`
    }`;
    let path =
      mockuse === 1
        ? "library/mock/test/create"
        : +type === libTypeIds.ASSIGNMENT
        ? `library/${type}/assignment${queryString}`
        : +type === libTypeIds.CONTENT
        ? `library/${type}/contententry${queryString}`
        : `library/${type}/test/create${queryString}`;
    navigate(path);
  };

  const onRowClick = (row) => {
    console.log("row:", row);
    navigate(
      mockuse === 1
        ? `library/mock/test/${row.id}`
        : `library/${type}/test/${row.id}${
            row.isVideoqns ? "?isVideoInterview=true" : ""
          }${
            row.aiMode ? "?isAiInterview=true" : ""
          }`,
    );
  };

  const onAdd = async () => {
    console.log("selected rows", selectedRow);
    try {
      setIsAdding(true);
      const { data } = await axios.post(
        `node/admin/learningpath/${lpId}/module/${moduleId}/assign-tests`,
        { type: type, tests: selectedRow, chapterId },
      );
      if (data.resultCode === RESULT_STATUS.SUCCESS) {
        navigate(
          `${baseRoute}/${lpId}/module/${moduleId}?chapterId=${chapterId}`,
        );
      } else {
        throw new Error(
          data.msg ??
            `Unable to add ${
              type === libTypeIds.CONTENT
                ? "content/s"
                : type === libTypeIds.ASSIGNMENT
                ? "assignment/s"
                : "test/s"
            } to path`,
        );
      }
    } catch (error) {
      console.error("error in add test/library to path", error);
      let isUnAuth = error.message.includes("403");
      setShowNotify({
        isUnAuth,
        title: isUnAuth ? "Warning !" : "Error !",
        msg: isUnAuth
          ? "You have been logged-out due to inactivity. Login again."
          : error.message ??
            `Unable to add ${
              type === libTypeIds.CONTENT
                ? "content/s"
                : type === libTypeIds.ASSIGNMENT
                ? "assignment/s"
                : "test/s"
            } to path`,
        show: true,
      });
    } finally {
      setIsAdding(false);
    }
  };

  const onBack = () => {
    navigate(`${baseRoute}/${lpId}/module/${moduleId}?chapterId=${chapterId}`);
    // let newDetails = JSON.parse(JSON.stringify(lpData2));

    // newDetails.isAddTest = false;
    // handleNavigateToLpAfterAddTest(newDetails);
  };

  if (!testName) return <Error404 />;

  return (
    <div className="admin-test-list-container">
      {/* <div className="admin-test-list-header-wrapper">
        <h2 className="admin-test-list-header">{testName}</h2>
        <div className="admin-test-list-header-left-wrapper">
          <Search
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
          />
          <InputDropdown
            placeholder="Select Topic"
            multiple
            options={topics}
            value={selectedTopics}
            onChange={setSelectedTopics}
            searchable
            className="admin-test-list-topic-dropdown"
          />
          <Button
            size="sm"
            hierarchy={{
              type: "primary",
              buttonText: `Create ${
                +type === libTypeIds.CONTENT
                  ? "Content"
                  : +type === libTypeIds.ASSIGNMENT
                  ? "Assignment"
                  : "Test"
              }`,
            }}
            onClick={onClick}
          />
        </div>
      </div> */}
      <Table
        className={`admin-test-list-table${
          [libTypeIds.CONTENT, libTypeIds.ASSIGNMENT].includes(parseInt(type))
            ? " admin-content-list-table"
            : mockuse === 1
            ? " admin-mock-list-table"
            : ""
        }`}
        columns={columns}
        selectable={selectable}
        selectedRows={selectedRow}
        data={filteredData}
        onRowClick={!selectable ? onRowClick : null}
        onRowSelect={(rows) => setSelectedRow(rows)}
        fixedHeader
      />
      {selectable ? (
        <div className="admin-test-list-btn-wrapper">
          <Button
            size="sm"
            hierarchy={{ type: "secondaryGrey", buttonText: "Back" }}
            onClick={onBack}
          />
          <Button
            size="sm"
            hierarchy={{
              type: "primary",
              buttonText: `${isAdding ? "Adding..." : "Add"}`,
            }}
            disable={!selectedRow.length || isAdding}
            onClick={onAdd}
          />
        </div>
      ) : null}
{(isLoaded || isSearching) && <Loading overlay />}
    </div>
  );
};

export default TestList;
